import React, { FunctionComponent } from 'react';
import { SbBlokData } from '@storyblok/react';
import { graphql, useStaticQuery } from 'gatsby';

import { IconListType, MaterialIcon, Typography } from '@savebetter/components';

import TrustPilotStar from '../svgs/TrustPilotStar';

import { PageSectionProps } from './PageSection';
import { Section } from './Section';

interface TrustBarItems {
  icon?: IconListType;
  title?: string;
  subTitle?: string;
  logo?: { url: string };
}

interface Blok extends SbBlokData {
  component: string;
  trustBarItems: Array<TrustBarItems>;
  hasPaddingBottom?: boolean;
  hasPaddingTop?: boolean;
  hasBackground?: boolean;
}

interface ReferenceBlok extends SbBlokData {
  reference: {
    content: {
      global: Array<Blok>;
    };
  };
}

interface TrustBarProps extends PageSectionProps {
  blok: Blok;
}
interface TrustBarReferenceProps extends PageSectionProps {
  blok: ReferenceBlok;
}

interface TrustPilotQuery {
  trustPilot: {
    numberOfReviews: {
      total: number;
    };
    score: {
      trustScore: number;
      stars: number;
    };
  };
}

const TrustPilotWidget = () => {
  const { trustPilot }: TrustPilotQuery = useStaticQuery(
    graphql`
      query trustPilotQuery {
        trustPilot {
          numberOfReviews {
            total
          }
          score {
            trustScore
            stars
          }
        }
      }
    `
  );
  return (
    <div>
      <a
        href="https://www.trustpilot.com/review/raisin.com/en-us"
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col items-center"
      >
        <Typography variant="h4" className="flex items-center gap-x-1">
          {trustPilot.score.trustScore}
          <TrustPilotStar />
          Trustpilot
        </Typography>
        <Typography variant="caption">{trustPilot.numberOfReviews.total} reviews</Typography>
      </a>
    </div>
  );
};

export const TrustBarReference: FunctionComponent<TrustBarReferenceProps> = ({ blok }) => {
  const reference_blok = blok.reference.content.global[0];

  return <TrustBar blok={reference_blok} />;
};

export const TrustBar: FunctionComponent<TrustBarProps> = ({ blok }) => {
  const { trustBarItems, hasPaddingBottom, hasPaddingTop, hasBackground } = blok;

  return (
    <Section hasPaddingBottom={hasPaddingBottom} hasPaddingTop={hasPaddingTop} hasBackground={hasBackground}>
      <div className="sb-section-content grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {trustBarItems.map(({ icon, title, subTitle, logo }, index) => {
          if (title === 'TrustPilotWidget') {
            return <TrustPilotWidget />;
          }

          if (logo) {
            return (
              <div className="flex flex-col items-center text-center justify-center m-auto">
                <img className="max-w-[200px] max-h-[40px]" src={logo.url} alt="logo" />
              </div>
            );
          }

          return (
            <div key={index} className="flex flex-col items-center text-center">
              <Typography variant="h4">
                {icon && <MaterialIcon name={icon} className="mr-2" />}
                {title}
              </Typography>
              <Typography variant="caption" className="ml-4">
                {subTitle}
              </Typography>
            </div>
          );
        })}
      </div>
    </Section>
  );
};
